import { useParams, useSearchParams } from "react-router-dom";

import { useAppContext } from "@/contexts/AppContext";
import { useAutoRefresh } from "@/hooks/dashboard/useAutoRefresh";
import { useDashboardRouteTenant } from "@/hooks/dashboard/useDashboardRouteTenant";
import { useMultiChartEvents, type MultiChartEvent } from "@/hooks/live/useMultiChartEvents";
import { resolveDashboardRouteScope } from "@/lib/dashboard/scope";
import type { DashboardScopeConfig } from "@/lib/dashboard/types";

/**
 * The event scope for a dashboard the user configured generically through
 * the editor's "Events" viewer control — as opposed to Event Monitor/Overview
 * (their own composition context, with remembered selection and an optional
 * protected-event tier) or Event Protection (its own campaign-derived list).
 * A plain "which recent event does this dashboard show" resolver, reading and
 * writing the same route binding every other dashboard scope already uses
 * (see `resolveDashboardRouteScope`), so it drops in wherever the tenant
 * scope and time range controls already work today.
 */
export function useGenericEventScope(scope: DashboardScopeConfig | undefined) {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { recents, pushRecentEvent, timezone } = useAppContext();
  const refreshTime = useAutoRefresh();
  const route = resolveDashboardRouteScope(scope, params, searchParams);
  const globalTenantIds = useDashboardRouteTenant(route.tenantId);
  const tenantIds = scope?.tenants === "all" ? [] : globalTenantIds;
  const eventsQuery = useMultiChartEvents(refreshTime, tenantIds, {
    includeAll: scope?.timeRange?.mode === "event-relative",
  });
  const events = eventsQuery.data ?? [];
  const selectedEventId = route.eventId ?? null;
  const selectedEvent = events.find((event) => event.event_id === selectedEventId) ?? null;
  const eventKey = scope?.bindings?.event_id?.queryParam ?? "event_id";

  function onEventChange(event: MultiChartEvent) {
    const query = new URLSearchParams(searchParams);
    query.set(eventKey, event.event_id);
    setSearchParams(query);
    pushRecentEvent(
      {
        event_id: event.event_id,
        event_label: event.event_label,
        collection_group: event.collection_group,
      },
      { view: "monitor" },
    );
  }

  function onClear() {
    const query = new URLSearchParams(searchParams);
    query.delete(eventKey);
    setSearchParams(query);
  }

  return {
    events,
    recents,
    selectedEvent,
    selectedEventId,
    timezone,
    loading: eventsQuery.isLoading,
    error: eventsQuery.isError,
    onEventChange,
    onClear,
    onRetry: () => void eventsQuery.refetch(),
  };
}
